import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AddVideoModal from './AddVideoModal';
import EditVideoModal from './EditVideoModal';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
  if (typeof textObj === 'object' && textObj !== null) {
    return textObj[lang] || textObj.en || '';
  }
  return typeof textObj === 'string' ? textObj : '';
}

// Helper function to extract YouTube video ID from embed URL
function getYouTubeVideoId(embedUrl) {
  if (!embedUrl) return null;
  // Handle embed URLs like https://www.youtube.com/embed/VIDEO_ID
  const embedMatch = embedUrl.match(/youtube\.com\/embed\/([^?&]+)/);
  if (embedMatch) return embedMatch[1];
  // Handle regular URLs as fallback
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = embedUrl.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

// Helper function to get YouTube thumbnail URL
function getYouTubeThumbnail(embedUrl) {
  const videoId = getYouTubeVideoId(embedUrl);
  if (videoId) {
    // Use maxresdefault for best quality, with fallback to hqdefault
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return null;
}

// Helper function to get embed URL with autoplay enabled
function getAutoplayEmbedUrl(embedUrl) {
  if (!embedUrl) return embedUrl;
  const separator = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${separator}autoplay=1`;
}

function VideoGallery() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingVideoId, setDownloadingVideoId] = useState(null);

  useEffect(() => {
    // Subscribe to videos collection - only YouTube videos from Firebase
    const q = query(collection(db, 'videos'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firebaseVideos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVideos(firebaseVideos);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching videos:", error);
      setVideos([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const categories = ['All', ...new Set(videos.map(v => v.category))];

  const filteredVideos = activeTab === 'All'
    ? videos
    : videos.filter(v => v.category === activeTab);

  const handleWatchClick = (video) => {
    setSelectedVideo(video);
  };

  const closePlayer = () => {
    setSelectedVideo(null);
  };

  const handleEditClick = (video, e) => {
    e.stopPropagation();
    setVideoToEdit(video);
    setIsEditModalOpen(true);
  };

  const handleDownload = (e, videoId) => {
    if (downloadingVideoId === videoId) {
      e.preventDefault();
      return;
    }

    setDownloadingVideoId(videoId);

    // Show toast
    toast.info(t('video_download_started'), {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });

    // Reset after 5 seconds to allow re-download if needed, but prevents accidental double-clicks
    setTimeout(() => {
      setDownloadingVideoId(null);
    }, 5000);
  };

  return (
    <div className="page-section">
      <div className="section-header">
        <div>
          <h2 className="section-title">{t('video_gallery')}</h2>
          <p className="section-subtitle">{t('videos_subtitle')}</p>
        </div>
        {isAdmin && (
          <button
            className="btn-primary-modern"
            onClick={() => setIsAddModalOpen(true)}
            style={{ height: 'fit-content' }}
          >
            {t('video_add_btn')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        {categories.map(category => (
          <button
            key={category}
            className={`tab-btn ${activeTab === category ? 'active' : ''}`}
            onClick={() => setActiveTab(category)}
          >
            {category === 'All' ? t('events_filter_all') : category}
          </button>
        ))}
      </div>

      {/* Video Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('video_loading')}</p>
        </div>
      ) : (
        <div className="grid-container">
          {filteredVideos.map(video => (
            <div className="card-modern video-card" key={video.id}>
              {/* Admin Edit Button */}
              {isAdmin && (
                <button
                  className="card-edit-btn"
                  onClick={(e) => handleEditClick(video, e)}
                  title={t('edit')}
                >
                  ✏️
                </button>
              )}
              <div
                className="video-thumbnail"
                style={{
                  background: video.customThumbnail
                    ? `url(${video.customThumbnail}) center/cover no-repeat`
                    : getYouTubeThumbnail(video.src)
                      ? `url(${getYouTubeThumbnail(video.src)}) center/cover no-repeat`
                      : video.thumbnailColor || 'var(--bg-secondary)'
                }}
                onClick={() => handleWatchClick(video)}
              >
                <div className="play-button"></div>
              </div>
              <h5 className="card-title-modern">{getLocalizedText(video.title, currentLang)}</h5>
              <div className="card-meta">
                <span className="card-meta-item">
                  <span className="card-meta-label">{video.category}</span>
                </span>
              </div>
              <p className="card-text-modern">{getLocalizedText(video.description, currentLang)}</p>
              <button
                className="btn-primary-modern"
                onClick={() => handleWatchClick(video)}
              >
                {t('video_watch')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="video-modal-overlay" onClick={closePlayer}>
          <div className="video-modal-content" onClick={e => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closePlayer}>×</button>
            <h3 className="card-title-modern" style={{ marginBottom: '1rem' }}>{getLocalizedText(selectedVideo.title, currentLang)}</h3>
            <div className="video-player-wrapper">
              {selectedVideo.type === 'youtube' ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={getAutoplayEmbedUrl(selectedVideo.src)}
                  title={getLocalizedText(selectedVideo.title, currentLang)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  width="100%"
                  height="100%"
                  controls
                  autoPlay
                  src={selectedVideo.src}
                  poster={selectedVideo.customThumbnail}
                  style={{ backgroundColor: '#000' }}
                >
                  {t('video_browser_not_supported') || 'Your browser does not support the video tag.'}
                </video>
              )}
            </div>
            {selectedVideo.type !== 'youtube' && (
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <a
                  href={`/api/videos/download/${selectedVideo.id}`}
                  className={`btn-primary-modern ${downloadingVideoId === selectedVideo.id ? 'disabled' : ''}`}
                  style={{
                    textDecoration: 'none',
                    opacity: downloadingVideoId === selectedVideo.id ? 0.7 : 1,
                    cursor: downloadingVideoId === selectedVideo.id ? 'not-allowed' : 'pointer',
                    pointerEvents: downloadingVideoId === selectedVideo.id ? 'none' : 'auto'
                  }}
                  onClick={(e) => handleDownload(e, selectedVideo.id)}
                >
                  {downloadingVideoId === selectedVideo.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'text-bottom', border: '0.15em solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spinner-border .75s linear infinite' }}></span>
                      {t('video_downloading')}
                    </>
                  ) : (
                    t('video_download')
                  )}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Video Modal */}
      <AddVideoModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          // Success toast or notification could go here
        }}
      />

      {/* Edit Video Modal */}
      <EditVideoModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setVideoToEdit(null);
        }}
        video={videoToEdit}
        onSuccess={() => {
          // Videos will auto-refresh via onSnapshot
        }}
      />
    </div>
  );
}

export default VideoGallery;
